/**
 * Service pour gérer les styles personnalisés des widgets
 */

import { css, CSSResult, unsafeCSS } from "lit";
import { WidgetVisualCustomization, WidgetFont } from "../types/widgets.d";

export class StyleService {
  /**
   * Génère les variables CSS personnalisées pour un widget
   */
  static generateCSSVariables(visual: WidgetVisualCustomization): string {
    // Convertir la couleur primaire en RGB pour créer une version avec opacité
    const primaryColor = visual.colors.primary;
    const rgb = this.hexToRgb(primaryColor);
    const primaryColorRgba = rgb
      ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.2)`
      : `rgba(0, 148, 219, 0.2)`;

    return `
      --widget-primary-color: ${primaryColor};
      --widget-primary-color-rgba-20: ${primaryColorRgba};
      --widget-secondary-color: ${visual.colors.secondary};
      --widget-background-color: "#f8f9fa";
      --widget-card-background-color: "#ffffff";
      --widget-title-font-family: ${this.getFontFamily(visual.typography.titleFont) || "Inter, sans-serif"};
      --widget-title-size: ${visual.typography.titleSize}px;
      --widget-title-color: ${visual.typography.titleColor || "#1a1a1a"};
      --widget-text-font-family: ${this.getFontFamily(visual.typography.textFont) || "Inter, sans-serif"};
      --widget-text-size: ${visual.typography.textSize}px;
      --widget-text-color: ${visual.typography.textColor || "#333333"};
    `;
  }

  /**
   * Convertit une couleur hexadécimale en RGB
   */
  private static hexToRgb(
    hex: string,
  ): { r: number; g: number; b: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  }

  /**
   * Génère le style CSS personnalisé complet
   */
  static generateCustomStyles(visual: WidgetVisualCustomization): CSSResult {
    const variables = this.generateCSSVariables(visual);

    let customCSS = "";
    if (visual.customCSS) {
      customCSS = visual.customCSS;
    }

    return css`
      :host {
        ${unsafeCSS(variables)}
      }
      ${unsafeCSS(customCSS)}
    `;
  }

  /**
   * Retourne la font-family CSS appropriée
   */
  static getFontFamily(font: WidgetFont): string {
    if (font === WidgetFont.INHERIT) {
      return "inherit";
    }

    // Map des polices avec fallbacks
    const fontFamilies: Record<string, string> = {
      [WidgetFont.ROBOTO]: '"Roboto", sans-serif',
      [WidgetFont.OPEN_SANS]: '"Open Sans", sans-serif',
      [WidgetFont.INTER]: '"Inter", sans-serif',
      [WidgetFont.MONTSERRAT]: '"Montserrat", sans-serif',
      [WidgetFont.LATO]: '"Lato", sans-serif',
      [WidgetFont.POPPINS]: '"Poppins", sans-serif',
      [WidgetFont.DM_SANS]: '"DM Sans", sans-serif',
      [WidgetFont.HELVETICA]: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      [WidgetFont.ARIAL]: "Arial, sans-serif",
    };

    return fontFamilies[font] || '"Inter", sans-serif';
  }

  /**
   * Charge les polices Google Fonts si nécessaire
   */
  static loadGoogleFonts(fonts: WidgetFont[]): void {
    const googleFonts = [
      WidgetFont.ROBOTO,
      WidgetFont.OPEN_SANS,
      WidgetFont.INTER,
      WidgetFont.MONTSERRAT,
      WidgetFont.LATO,
      WidgetFont.POPPINS,
      WidgetFont.DM_SANS,
    ];

    const fontsToLoad = fonts.filter((f) => googleFonts.includes(f));

    if (fontsToLoad.length === 0) return;

    // Vérifier si les polices sont déjà chargées
    const existingLink = document.querySelector("link[data-apidae-fonts]");
    if (existingLink) return;

    // Construire l'URL Google Fonts
    const fontNames = fontsToLoad.map((f) => {
      const name = f.replace(/ /g, "+");
      return `${name}:400,600,700`;
    });

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?${fontNames.map((f) => `family=${f}`).join("&")}&display=swap`;
    link.setAttribute("data-apidae-fonts", "true");
    document.head.appendChild(link);
  }

  /**
   * Applique les styles Leaflet pour les cartes
   */
  static getLeafletStyles(): string {
    return `
      .leaflet-container {
        font-family: var(--widget-font-family, 'Inter', sans-serif);
      }

      .leaflet-popup-content-wrapper {
        border-radius: 8px;
      }

      .leaflet-popup-content {
        margin: 12px;
        font-size: var(--widget-text-size, 14px);
      }

      .leaflet-marker-icon {
        filter: hue-rotate(0deg);
      }
    `;
  }
}
